/**
 * Attachments component.
 * @module components/Attachments/Attachments
 */

import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Segment, Grid, Header, Container } from 'semantic-ui-react';
import { defineMessages, useIntl, FormattedMessage, } from 'react-intl';
import { searchContent } from '@plone/volto/actions';
import { UniversalLink, Image } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faEye, faFileLines } from '@fortawesome/free-solid-svg-icons';
import { keys, isEmpty, map, filter } from 'lodash';
import { BodyClass } from '@plone/volto/helpers';
import Lightbox from '../Lightbox/Lightbox';


const Attachments = props => {
  const content = props;
  const dispatch = useDispatch();
  const intl = useIntl();
  const attachments = useSelector((state) => state.search.subrequests.attachments?.items);

  const files = useMemo(() => 
    filter(attachments, (item) => item['@type'] === 'File'), 
    [attachments]
  );

  const links = useMemo(() => 
    filter(attachments, (item) => item['@type'] === 'Link'), 
    [attachments]
  );

  const images = useMemo(() => 
    filter(attachments, (item) => item['@type'] === 'Image'), 
    [attachments]
  );

  useEffect(() => {
    dispatch(
      searchContent(
        flattenToAppURL(content['@id']),
        {
          portal_type: ['Image', 'File', 'Link'],
          sort_on: 'getObjPositionInParent',
          metadata_fields: ['getObjSize', 'mime_type', 'remoteUrl']
        },
        'attachments',
      ),
    );
  }, [dispatch]);

  return !isEmpty(attachments) ? (
    <>
      <BodyClass className='hasRelatedContent' />
       
        <Segment as="section" basic id="related-content" className="relatedContent px-0">
          <h2 className="visually-hidden" ><FormattedMessage id="Related content" defaultMessage="Related content" /></h2>

          {!isEmpty(images) && (
            <Segment as="section" basic className="relatedContent__section relatedMedia px-0">
              <Header as="h3" className="relatedContent__title h5 mb-4"><FormattedMessage id="Related images" defaultMessage="Related images" /></Header>
              <Lightbox images={images} />
            </Segment>
          )}

          {!isEmpty(files) && (
            <Segment as="section" basic className="relatedContent__section px-0 my-0">
              <Header as="h3" className="relatedContent__title h5"><FormattedMessage id="Related documents" defaultMessage="Related documents" /></Header>
              <Grid columns={12}>
                <Grid.Row as='ul' className='list-unstyled my-0'>
                  {map(files, (item, index) => (
                    <Grid.Column key={index} mobile={12} tablet={6} computer={6} largeScreen={5} className='mb-4' as="li" >
                      <UniversalLink
                        className="relatedItem"
                        href={flattenToAppURL(item['@id']) + "/@@display-file/file"}>
                        <span className="relatedItem__icon" >
                          <FontAwesomeIcon icon={faFileLines} />
                        </span>
                        <span className="relatedItem__text">
                          <span className="relatedItem__title">{item.title}</span>
                          <span className="relatedItem__size"><FormattedMessage id="Format" defaultMessage="Format" />: {item.mime_type.split('/').pop()} - {item.getObjSize}</span>
                        </span>
                      </UniversalLink>
                    </Grid.Column>
                  ))}
                </Grid.Row>
              </Grid>
            </Segment>
          )}

          {!isEmpty(links) && (
            <Segment as="section" basic className="relatedContent__section px-0 my-0 pb-0">
              <Header as="h3" className="relatedContent__title h5 mb-4"><FormattedMessage id="Related links" defaultMessage="Related links" /></Header>
              <Grid columns={12}>
                <Grid.Row as="ul" className='list-unstyled my-0'>
                  {map(links, (item, index) => (
                    <Grid.Column className=' mb-4' key={index} mobile={12} tablet={6} computer={6} largeScreen={6} widescreen={6} as='li'>
                      <UniversalLink
                        className="relatedItem"
                        href={(item['remoteUrl'])}>
                        <span className="relatedItem__icon" >
                          <FontAwesomeIcon icon={faLink} />
                        </span>
                        <span className="relatedItem__title">{item.title}</span>
                      </UniversalLink>
                    </Grid.Column>
                  ))}
                </Grid.Row>
              </Grid>
            </Segment>
          )}

        </Segment>
 
    </>
  ) : (
    <></>
  );
};

export default Attachments;